import React, { useMemo } from 'react';
import get from 'lodash/get';
import StudentColumn from '../components/StudentColumn/StudentColumn';
import Column from '../components/Column/Column';
import {
  WIDGET_TYPE_SRL_ACADEMICS_CHART,
  WIDGET_TYPE_SRL_CHALLENGE_AND_RECALL_CHART,
  WIDGET_TYPE_SRL_CHALLENGES_CHART,
  WIDGET_TYPE_SRL_CONFIDENCE_CHART,
  WIDGET_TYPE_SRL_ENVIRONMENT_AND_MINDSET,
  WIDGET_TYPE_SRL_HELP_AND_COLLABORATION,
  WIDGET_TYPE_SRL_HOURS_CHART,
  WIDGET_TYPE_SRL_SPACING_PACING_CHART
} from '../../../../../constants';
import { normalizeString } from '../../../../../utils';
import { getConfidenceHeaders, getHourHeaders, getPositiveCounters } from '../utils/headerUtils';

export function generateColumns({
    TABLE_CONSTANTS,
    initialColumns,
    widgetType,
    isSRLIntroSurvey,
    isSRLEnglishVersion,
    searchResponseTerm,
    selectedTags,
    courseId,
    courseShortId,
    productModelId,
    assignmentId,
    activityId,
    tool,
  }) {
  const fixedWidthColumnIndices = TABLE_CONSTANTS.FIXED_WIDTH_COLUMN_INDICES || [];
  const fixedColumnsWidthList = TABLE_CONSTANTS.FIXED_COLUMNS_WIDTH_LIST || [];
  const columnHeaderAliasList = TABLE_CONSTANTS.COLUMN_HEADER_ALIAS_LIST || [];
  const fixedColumnWidth = TABLE_CONSTANTS.FIXED_COLUMN_WIDTH;
  const centerTextIndices = TABLE_CONSTANTS.CENTER_ALIGNED_COLUMNS_INDICES;
  const nonFixedColumns = initialColumns.length - fixedWidthColumnIndices.length;
  const flexBasis = nonFixedColumns > 0 ? `${100 / nonFixedColumns}%` : 'auto';

  const launchProps = {
    courseId,
    courseShortId,
    productModelId,
    assignmentId,
    activityId,
    tool,
  };

  return initialColumns.map((column, index) => {
    let width = fixedColumnsWidthList[index];
    if (!width) {
      width =
        fixedWidthColumnIndices &&
        fixedWidthColumnIndices.includes(index) &&
        fixedColumnWidth;
    }
    let name = column;
    if (columnHeaderAliasList.length > 0) {
      name = columnHeaderAliasList[index] ?? name;
    }
    const centerText = centerTextIndices && centerTextIndices.includes(index);
    const accessor = index === 0 ? 'student' : `answer_${index - 1}`;
    const emptyHeader = get(TABLE_CONSTANTS, `ALLOW_DISPLAY_EMPTY_COLUMNS_INDICES`, []).includes(index);
    return {
      name: column,
      emptyHeader,
      width,
      centerText,
      accessor,
      flexBasis: width ? 'auto' : flexBasis,
      renderLabel: () => name,
      render: (row) =>
        (index === 0 ? (
          <StudentColumn row={row} launchProps={launchProps} />
        ) : (
          <Column
            columnIndex={index}
            columnValue={row[accessor]}
            constants={TABLE_CONSTANTS}
            centerText={centerText}
            widgetType={widgetType}
            isSRLIntroSurvey={isSRLIntroSurvey}
            isSRLEnglishVersion={isSRLEnglishVersion}
            searchResponseTerm={searchResponseTerm}
            selectedTags={selectedTags}
          />
        )),
    };
  });
}

export const useTableData = ({
    TABLE_CONSTANTS,
    initialColumns,
    initialRows,
    widgetType,
    isSRLIntroSurvey,
    isSRLEnglishVersion,
    selectedSortSettings,
    searchResponseTerm,
    selectedTags,
    insightsData,
    courseId,
    courseShortId,
    productModelId,
    assignmentId,
    activityId,
    tool,
  }) => {
  const { sortColumn, sortDirection } = selectedSortSettings;
  const columns = generateColumns({
    TABLE_CONSTANTS,
    initialColumns,
    widgetType,
    isSRLIntroSurvey,
    isSRLEnglishVersion,
    searchResponseTerm,
    selectedTags,
    courseId,
    courseShortId,
    productModelId,
    assignmentId,
    activityId,
    tool,
  });

  const getDefaultSortValue = (sortColumnName) => {
    const sortColumnIndex = columns.findIndex(
        (col) => col.accessor === sortColumnName
    );
    if (TABLE_CONSTANTS.GRADE_COLUMN_INDICES?.includes(sortColumnIndex)) return 'a';
    if (
        columns[TABLE_CONSTANTS.CONFIDENCE_CHANGE_COLUMN_IDX]?.accessor ===
        sortColumnName
    ) return -6;
    if (widgetType === WIDGET_TYPE_SRL_HOURS_CHART) return -999;
    return 0;
  };

  const getSpecialStringValue = (value) => {
    const upperValue = !Array.isArray(value) && value.toUpperCase();
    if (
        widgetType === WIDGET_TYPE_SRL_HELP_AND_COLLABORATION &&
        !isSRLIntroSurvey &&
        upperValue === 'NEVER'
    ) return 0.1;
    if (
        widgetType === WIDGET_TYPE_SRL_ACADEMICS_CHART &&
        !isSRLIntroSurvey &&
        upperValue === 'BELOW 60'
    ) return 59;
    if (
        widgetType === WIDGET_TYPE_SRL_ACADEMICS_CHART &&
        isSRLIntroSurvey &&
        upperValue === 'OTHER'
    ) return 6;
    return null;
  };

  const getSortValue = (row, sortColumnName) => {
    const sortColumnIndex = columns.findIndex(
        (col) => col.accessor === sortColumnName
    );
    if (sortColumnIndex === -1) return 0;

    const value = row[sortColumnName];
    if (value === null) return getDefaultSortValue(sortColumnName);

    if (sortColumnName === 'student') {
      const studentFullName = `${row.student.lastName.toUpperCase()} ${row.student.firstName.toUpperCase()}`;
      return normalizeString(studentFullName);
    }

    if (TABLE_CONSTANTS.GRADE_COLUMN_INDICES?.includes(sortColumnIndex)) {
      return value.toUpperCase();
    }

    if (isNaN(parseFloat(value))) {
      return getSpecialStringValue(value) || value.length;
    }

    return parseFloat(value);
  };

  const compare = (a, b, sortColumnName, secondarySortOrder = ['student']) => {
    const aValue = getSortValue(a, sortColumnName);
    const bValue = getSortValue(b, sortColumnName);

    if (aValue !== bValue) {
      return aValue < bValue ? -1 : 1;
    }

    // If values are equal, move to the next sort column
    const nextSortColumn = secondarySortOrder.find(
        (colName) => colName !== sortColumnName
    );
    if (nextSortColumn) {
      return compare(a, b, nextSortColumn, secondarySortOrder);
    }

    return 0;
  };

  const filteredSecondarySortOrder = TABLE_CONSTANTS.SECONDARY_SORT_ORDER?.map(
      (index) => columns[index - 1]?.accessor
  ).filter((column) => column !== selectedSortSettings.sortColumn);

  const secondarySortOrder = filteredSecondarySortOrder || ['student'];

  const rows = useMemo(() => [...initialRows].sort((a, b) => {
    const compareResult = compare(a, b, sortColumn, secondarySortOrder);
    return sortDirection === 'asc' ? compareResult : -compareResult;
  }), [sortColumn, sortDirection, initialRows]);

  let positiveCounters = {};
  if (
    [
      WIDGET_TYPE_SRL_ENVIRONMENT_AND_MINDSET,
      WIDGET_TYPE_SRL_HELP_AND_COLLABORATION,
      WIDGET_TYPE_SRL_SPACING_PACING_CHART,
      WIDGET_TYPE_SRL_CHALLENGE_AND_RECALL_CHART,
    ].includes(widgetType) ||
    (widgetType === WIDGET_TYPE_SRL_CHALLENGES_CHART && !isSRLIntroSurvey)
  ) {
    positiveCounters = getPositiveCounters(rows, columns, TABLE_CONSTANTS);
  }

  const confidenceSubHeaders = widgetType === WIDGET_TYPE_SRL_CONFIDENCE_CHART
    ? getConfidenceHeaders(insightsData, TABLE_CONSTANTS)
    : null;

  const hoursSubHeaders = widgetType === WIDGET_TYPE_SRL_HOURS_CHART
    ? getHourHeaders(insightsData, TABLE_CONSTANTS)
    : null;

  return {
    columns,
    rows,
    positiveCounters,
    confidenceSubHeaders,
    hoursSubHeaders,
    count: rows.length,
  };
};
